import React, { useState, useEffect } from "react";
import HomePage from "./pages/HomePage";
import ExperiencesPage from "./pages/ExperiencesPage";
import ServicesPage from "./pages/ServicesPage";
import ProjectsPage from "./pages/ProjectsPage";
import CapabilitiesPage from "./pages/CapabilitiesPage";
import ContactPage from "./pages/ContactPage";
import UseExecutionPage from "./pages/UseExecutionPage";
import CompanyResearchPage from "./pages/use_cases/CompanyResearchPage";
import MusicVisualizerPage from "./pages/use_cases/MusicVisualizerPage";
import LinkCollector from "./pages/LinkCollector";
import FindJobsPage from "./pages/use_cases/FindJobsPage";
import AssetsPage from "./pages/AssetsPage";
import { ThemeContext } from "./theme";

const navItems = [
  { id: "home", label: "Home" },
  { id: "experiences", label: "Experiences" },
  { id: "services", label: "Services" },
  { id: "projects", label: "Projects" },
  { id: "capabilities", label: "Capabilities" },
  { id: "contact", label: "Contact Us" }
];
const EXPERIENCE_RELATED_PAGES = new Set(["companyResearch", "musicVisualizer", "assets"]);

const pageMap = {
  home: HomePage,
  experiences: ExperiencesPage,
  musicVisualizer: MusicVisualizerPage,
  assets: AssetsPage,
  services: ServicesPage,
  projects: ProjectsPage,
  capabilities: CapabilitiesPage,
  companyResearch: CompanyResearchPage,
  contact: ContactPage,
  useExecution: UseExecutionPage,
  findJobs: FindJobsPage,
  linkCollector: LinkCollector
};

export default function App() {
  const getInitialPage = () => {
    if (typeof window === "undefined") return "home";

    const hash = window.location.hash.slice(1);
    if (hash && pageMap[hash]) return hash;

    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    if (pageParam && pageMap[pageParam]) return pageParam;

    return "home";
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1366
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "retro";
    return window.sessionStorage.getItem("twodotsTheme") || "retro";
  });
  const Page = pageMap[currentPage] || HomePage;
  const isMobile = windowWidth < 768;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem("twodotsTheme", theme);
  }, [theme]);

  const handlePageChange = (pageId) => {
    setCurrentPage(pageId);
    if (typeof window !== "undefined") {
      window.location.hash = pageId;
    }
  };

  const themeOverrides = THEME_OVERRIDES[theme] || {};
  const pageShellStyle = { ...styles.pageShell, ...(themeOverrides.pageShell || {}) };
  const navBarStyle = { ...styles.navBar, ...(themeOverrides.navBar || {}) };
  const navButtonBaseStyle = { ...styles.navButton, ...(themeOverrides.navButton || {}) };
  const navButtonActiveStyle = {
    ...styles.navButtonActive,
    ...(themeOverrides.navButtonActive || {})
  };
  const mobileNavListStyle = {
    ...styles.mobileNavList,
    ...(themeOverrides.mobileNavList || {})
  };

  const renderNavButton = (item) => {
    const isActive =
      currentPage === item.id || (item.id === "experiences" && EXPERIENCE_RELATED_PAGES.has(currentPage));
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => handlePageChange(item.id)}
        style={{
          ...navButtonBaseStyle,
          ...(isActive ? navButtonActiveStyle : {})
        }}
      >
        {item.label}
      </button>
    );
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <div data-theme={theme} style={pageShellStyle}>
        <style>{`
          [data-theme="retro"] * {
            color: #3d2911 !important;
          }
          [data-theme="retro"] a {
            color: #3d2911 !important;
          }
          [data-theme="retro"] div[class*="capture"] {
            color: inherit !important;
          }
          [data-theme="retro"] p,
          [data-theme="retro"] h1,
          [data-theme="retro"] h2,
          [data-theme="retro"] h3,
          [data-theme="retro"] h4,
          [data-theme="retro"] h5,
          [data-theme="retro"] h6,
          [data-theme="retro"] span,
          [data-theme="retro"] li,
          [data-theme="retro"] button {
            color: #3d2911 !important;
          }
        `}</style>
        <header style={{ ...navBarStyle, ...(isMobile ? styles.navBarMobile : {}) }}>
          {isMobile ? (
            <>
              <div style={styles.mobileNavBar}>
                <span style={styles.mobileNavTitle}>Navigation</span>
                <button
                  type="button"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  style={styles.mobileToggle}
                  aria-expanded={menuOpen}
                >
                  ☰
                </button>
              </div>
              {menuOpen && (
                <div style={mobileNavListStyle}>
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        handlePageChange(item.id);
                        setMenuOpen(false);
                      }}
                      style={{
                        ...navButtonBaseStyle,
                        ...styles.navButtonMobile,
                        ...(item.id === currentPage ||
                        (item.id === "experiences" && EXPERIENCE_RELATED_PAGES.has(currentPage))
                          ? navButtonActiveStyle
                          : {})
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            navItems.map(renderNavButton)
          )}
        </header>
        <main style={styles.pageContent}>
          <Page
            {...(currentPage === "home"
              ? {
                  onLaunchAssets: () => handlePageChange("assets")
                }
              : {})}
            {...(currentPage === "experiences"
              ? {
                  onLaunchUseExecution: () => handlePageChange("useExecution"),
                  onLaunchCompanyResearch: () => handlePageChange("companyResearch"),
                  onLaunchFindJobs: () => handlePageChange("findJobs"),
                  onLaunchMusicVisualizer: () => handlePageChange("musicVisualizer")
                }
              : {})}
          />
        </main>
      </div>
    </ThemeContext.Provider>
  );
}

const styles = {
  pageShell: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0F0D12, #12061C 40%, #050810 100%)",
    color: "white",
    fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif"
  },
  navBar: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    padding: "16px 0",
    position: "sticky",
    top: 0,
    zIndex: 60,
    background: "rgba(5, 8, 18, 0.75)",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(12px)"
  },
  navButton: {
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 999,
    padding: "10px 22px",
    background: "transparent",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.25s ease"
  },
  navButtonMobile: {
    width: "100%",
    textAlign: "left",
    padding: "12px 16px 12px 16px",
    borderRadius: 10,
    fontSize: 16
  },
  navBarMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    padding: "8px 0",
    gap: 6
  },
  mobileNavBar: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 16px"
  },
  mobileNavTitle: {
    fontSize: 12,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.75)"
  },
  mobileToggle: {
    border: "1px solid rgba(255,255,255,0.45)",
    background: "transparent",
    color: "white",
    borderRadius: 8,
    padding: "4px 12px",
    fontSize: 20,
    cursor: "pointer"
  },
  mobileNavList: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 16px 16px",
    background: "rgba(5, 8, 18, 0.9)",
    borderTop: "1px solid rgba(255,255,255,0.08)"
  },
  navButtonActive: {
    borderColor: "#B86FFF",
    background: "linear-gradient(120deg, rgba(255,102,97,0.25), rgba(184,111,255,0.25))",
    boxShadow: "0 12px 30px rgba(184,111,255,0.25)"
  },
  pageContent: {
    paddingTop: 16,
    position: "relative",
    minHeight: "90vh"
  }
};

const THEME_OVERRIDES = {
  retro: {
    pageShell: {
      background: "#f5ead3",
      color: "#3d2911",
      fontFamily: "'Courier New', Courier, 'IBM Plex Mono', monospace"
    },
    navBar: {
      background: "rgba(245, 229, 201, 0.95)",
      borderBottom: "1px solid rgba(61, 41, 17, 0.35)"
    },
    navButton: {
      color: "#3d2911",
      borderColor: "rgba(61, 41, 17, 0.6)"
    },
    navButtonActive: {
      borderColor: "#b6833e",
      background: "rgba(182, 131, 62, 0.2)",
      boxShadow: "0 12px 30px rgba(182, 131, 62, 0.25)"
    },
    mobileNavList: {
      background: "rgba(245, 229, 201, 0.95)",
      borderTop: "1px solid rgba(61, 41, 17, 0.35)"
    }
  }
};
